package com.rainbowclubs.rainbow.presentation.membership

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.models.Club
import com.rainbowclubs.rainbow.data.models.MembershipRequest
import com.rainbowclubs.rainbow.domain.use_cases.club.CacheSelectedClubUseCase
import com.rainbowclubs.rainbow.domain.use_cases.club.GetClubUseCase
import com.rainbowclubs.rainbow.domain.use_cases.membership.CreateMembershipUseCase
import com.rainbowclubs.rainbow.domain.use_cases.user.GetUserUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch

class MembershipDataViewModel(
    private val clubId: String,
    private val getClubUseCase: GetClubUseCase,
    private val getUserUseCase: GetUserUseCase,
    private val createMembershipUseCase: CreateMembershipUseCase,
    private val cacheSelectedClubUseCase: CacheSelectedClubUseCase,
) : ViewModel() {
    sealed class NavigationEvent {
        data class Home(val clubId: String) : NavigationEvent()
    }

    val screenState: StateFlow<ScreenState<Club>>
        field: MutableStateFlow<ScreenState<Club>> = MutableStateFlow(ScreenState.Loading)

    private val navigationIntentChannel = Channel<NavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<NavigationEvent> = navigationIntentChannel.receiveAsFlow()

    fun fetchData() {
        viewModelScope.launch {
            if (clubId.isBlank()) {
                screenState.value =
                    ScreenState.Error(IllegalArgumentException("Club selection is required"))
                return@launch
            }
            screenState.value = ScreenState.Loading
            getClubUseCase.invoke(clubId).onSuccess {
                screenState.value = ScreenState.Content(it)
            }.onFailure {
                screenState.value = ScreenState.Error(it)
            }
        }
    }

    fun submitMembershipData(data: Map<String, String>) {
        val currentContent = screenState.value as? ScreenState.Content<Club> ?: return
        val club = currentContent.data
        val user = getUserUseCase.invoke() ?: run {
            screenState.value = ScreenState.Error(Exception("User not found"))
            return
        }

        viewModelScope.launch {
            screenState.value = ScreenState.Loading
            val request = MembershipRequest(
                userDisplayName = user.name ?: "Unknown",
                userId = user.id ?: "",
                clubId = club.id,
                clubName = club.name,
                membershipData = data,
            )

            createMembershipUseCase.invoke(request).onSuccess {
                cacheSelectedClubUseCase(club.id)
                navigationIntentChannel.trySend(NavigationEvent.Home(clubId = club.id))
            }.onFailure {
                screenState.value = ScreenState.Error(it)
            }
        }
    }
}
